/* Desktop hamburger menu overlay (>=1025px).
   Click burger -> opens full-screen panel and morphs to an X.
   Click X (same button) -> closes panel and morphs back.
   Hover top-level link -> activates matching right-side flyout. */

class DesktopMenuOverlay extends HTMLElement {
  constructor() {
    super();

    this.trigger = this.querySelector('.desktop-menu-overlay__trigger');
    this.panel   = this.querySelector('.desktop-menu-overlay__panel');
    this.items   = Array.from(this.querySelectorAll('.desktop-menu-overlay__primary-item'));
    this.flyouts = Array.from(this.querySelectorAll('.desktop-menu-overlay__flyout'));
    this.mq      = window.matchMedia('(min-width: 1025px)');

    this._onTriggerClick = this.toggle.bind(this);
    this._onKeyDown      = this.handleKey.bind(this);
    this._onPanelClick   = this.handlePanelClick.bind(this);

    this.bindEvents();
  }

  bindEvents() {
    if (this.trigger) this.trigger.addEventListener('click', this._onTriggerClick);
    if (this.panel)   this.panel.addEventListener('click', this._onPanelClick);

    this.items.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        this.cancelPendingClear();
        this.activateFlyout(item);
      });
      item.addEventListener('focusin', () => this.activateFlyout(item));
    });

    // Track hover on the WHOLE nav grid (both columns + the gutter between
    // them) instead of just the primary list. The primary <li>s shrink-wrap
    // to text, and there's a ~20px dead-zone between the column's right
    // padding and the flyout's left padding -- if we listened on .primary
    // only, crossing that gutter on the way to the submenu would fire
    // mouseleave and drop the active state. Listening on the nav grid means
    // the cursor never "exits" until it leaves the menu region entirely.
    const nav = this.querySelector('.desktop-menu-overlay__nav');
    if (nav) {
      nav.addEventListener('mouseleave', () => this.scheduleClear());
      nav.addEventListener('mouseenter', () => this.cancelPendingClear());
    }
  }

  scheduleClear() {
    this.cancelPendingClear();
    this._clearTimer = window.setTimeout(() => {
      this._clearTimer = null;
      this.clearActive();
    }, 140);
  }

  cancelPendingClear() {
    if (this._clearTimer) {
      window.clearTimeout(this._clearTimer);
      this._clearTimer = null;
    }
  }

  clearActive() {
    this.classList.add('no-flyout');
    this.items.forEach((i) => i.classList.remove('is-active'));
    this.flyouts.forEach((f) => {
      f.classList.remove('is-active');
      f.setAttribute('aria-hidden', 'true');
    });
  }

  toggle() {
    if (this.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this.mq.matches) return;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'true');
      this.trigger.setAttribute('aria-label', 'Close menu');
    }
    document.body.classList.add('desktop-menu-overlay-open');
    document.addEventListener('keydown', this._onKeyDown);
    // Do NOT auto-activate a flyout on open. The user explicitly asked
    // for no item to look "active" when nothing is being hovered. Each
    // primary item only lights up its underline on hover; flyouts only
    // appear when the user hovers a primary item that has a data-panel.
  }

  close() {
    this.cancelPendingClear();
    this.classList.remove('is-open');
    this.classList.remove('no-flyout');
    this.setAttribute('aria-hidden', 'true');
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-label', 'Open menu');
      this.trigger.focus({ preventScroll: true });
    }
    document.body.classList.remove('desktop-menu-overlay-open');
    document.removeEventListener('keydown', this._onKeyDown);
    // Drop active state on all items and flyouts so nothing sticks around
    // marked-active while the menu is closed (also avoids the previous
    // pointer-events:auto-on-closed-flyout bug).
    this.items.forEach((i) => i.classList.remove('is-active'));
    this.flyouts.forEach((f) => {
      f.classList.remove('is-active');
      f.setAttribute('aria-hidden', 'true');
    });
  }

  handleKey(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  /* Clicks on the panel's empty backdrop close the overlay. Clicks on nav
     content bubble up to the panel but with a non-panel target, so we
     only act when target === panel. */
  handlePanelClick(event) {
    if (event.target === this.panel) this.close();
  }

  activateFlyout(item) {
    if (!item) return;
    const key = item.dataset.panel;

    // ALWAYS mark this item as the active one -- even items without a
    // data-panel (Home) get .is-active so their underline shows on hover.
    this.items.forEach((i) => i.classList.toggle('is-active', i === item));

    // Flyout column behavior:
    // - If the hovered item has a data-panel, show that flyout.
    // - If it does not (Home), hide all flyouts AND mark the whole overlay
    //   .no-flyout so CSS can suppress the entire right-column area --
    //   defense in depth against any individual flyout still rendering
    //   stale content during its fade-out.
    this.classList.toggle('no-flyout', !key);
    this.flyouts.forEach((f) => {
      const match = !!key && f.dataset.flyout === key;
      f.classList.toggle('is-active', match);
      f.setAttribute('aria-hidden', match ? 'false' : 'true');
    });
  }
}

if (!customElements.get('desktop-menu-overlay')) {
  customElements.define('desktop-menu-overlay', DesktopMenuOverlay);
}
